export type SeatToken = {
  eventCode: string;
  section: string;
  row: string;
  firstSeat: number;
  lastSeat: number;
};

export type SeatGroup = { section: string; row: string; seats: number[] };

/** Parse a single raw seat token by position.
 *  Token: "eventCode_section_row_firstSeat_lastSeat[_extra...]".
 *  Returns null for malformed tokens (fewer than 5 parts / non-numeric seats). */
export function parseSeatToken(token: string): SeatToken | null {
  const parts = token.trim().split("_");
  if (parts.length < 5) return null;

  const [eventCode, section, row, firstSeat, lastSeat] = parts;
  const first = Number(firstSeat);
  const last = Number(lastSeat);
  if (Number.isNaN(first) || Number.isNaN(last)) return null;

  return { eventCode, section, row, firstSeat: first, lastSeat: last };
}

/** Parse comma-separated seat tokens, grouping seat numbers by section + row
 *  (first-seen order). Malformed tokens are skipped, never thrown. */
export function parseSeatTokens(raw: string): SeatGroup[] {
  const groups: SeatGroup[] = [];

  for (const token of raw.split(",")) {
    const parsed = parseSeatToken(token);
    if (!parsed) continue;

    const { section, row, firstSeat, lastSeat } = parsed;
    let group = groups.find((g) => g.section === section && g.row === row);
    if (!group) {
      group = { section, row, seats: [] };
      groups.push(group);
    }
    for (let seat = firstSeat; seat <= lastSeat; seat++) group.seats.push(seat);
  }

  return groups;
}

/** Collapse seat numbers into compact ranges: [4, 5, 6, 8] -> "4-6, 8". */
export function collapseSeatRanges(seats: number[]): string {
  const sorted = [...new Set(seats)].sort((a, b) => a - b);
  const ranges: string[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const start = sorted[i];
    while (sorted[i + 1] === sorted[i] + 1) i++;
    ranges.push(start === sorted[i] ? `${start}` : `${start}-${sorted[i]}`);
  }

  return ranges.join(", ");
}

/** Formats raw seat tokens for display, e.g. "Section 109, Row 1, Seats 4-6".
 *  Multiple section/row groups are separated by " | ". */
export function formatSeatLocation(raw: string): string {
  return parseSeatTokens(raw)
    .map(({ section, row, seats }) => {
      const label = seats.length > 1 ? "Seats" : "Seat";
      return `Section ${section}, Row ${row}, ${label} ${collapseSeatRanges(seats)}`; // prettier-ignore
    })
    .join(" | ");
}

/** Available seat tokens = seatsToSell minus seatsSold, comma-joined.
 *  (Relocated from buyer/listing/utils.listing.ts — single source of truth.) */
export function getAvailableSeatTokens(
  seatsToSellArray?: string[],
  seatsSoldArray?: string[]
): string {
  const sold = new Set(seatsSoldArray ?? []);
  return (seatsToSellArray ?? []).filter((t) => !sold.has(t)).join(",");
}
